import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { TemplatePageModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/templatePage/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/templatePage/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/templatePage/FETCH_FAILURE'

const UPDATE_REQUEST = 'realbase/templatePage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/templatePage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/templatePage/UPDATE_FAILURE'

export type TemplatePageModuleState = {
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TemplatePageModuleState = {
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTemplatePages(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().templatePages.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/template_pages.json', options)
      .then(({ data }: { data: TemplatePageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTemplatePage(templatePageId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.templatePages.loadedForKeys]
    const loadedIds = [...state.templatePages.loadedIds]

    if (templatePageId && !loadedIds.includes(templatePageId)) loadedIds.push(templatePageId)
    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/template_pages/${templatePageId}.json`, options)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type CheckTemplatePageUsageData = {
  page_ids: number[],
  unused_page_ids: number[],
}

export function checkTemplatePageUsage(
  templatePageIds: number[],
  options: EntityOptions = {},
) {
  const requestOptions = {
    ...options,
    template_page_ids: templatePageIds,
  }

  const promise = api('/template_pages/check_usage.json', requestOptions)
    .then(({ data }: { data: CheckTemplatePageUsageData }) => ({ success: true as const, data }))
    .catch((data) => {
      const errors = handleError(data)
      return { success: false as const, errors }
    })

  return promise
}

export function bulkUpdatePermissions(
  templatePage: UpdateParams<TemplatePageModel>,
  permissions: {
    agency_ids: number[],
  },
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      template_page_id: templatePage.id,
      permissions,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_pages/${templatePage.id}/bulk_update_permissions.json`, options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TemplatePageModuleState = initialState,
  action: Action = { type: '' },
): TemplatePageModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
